import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * LA PHOTO D'UN ÉTUDIANT — chargée AVEC le jeton, puis mise en cache
 * ==================================================================
 * Lot 1.5.
 *
 * ── ⚠️ POURQUOI CE COMPOSANT EXISTE, ET CE QU'IL CORRIGE ────────────────────
 * `etudiants.photo_url` désigne une route de l'API : `/api/etudiants/:id/photo`.
 * Un `<img src="/api/...">` écrit tel quel NE FONCTIONNE PAS ici, et le mode de
 * panne est silencieux : le navigateur émet la requête LUI-MÊME, sans passer par
 * axios, donc **sans l'en-tête `Authorization`**. L'authentification de ce projet
 * vit dans un jeton en `localStorage`, pas dans un cookie — le serveur répond
 * donc 401 et l'image reste vide. Aucune erreur à l'écran, aucune dans la
 * console : juste des vignettes grises.
 *
 * Mesuré à l'image le 2026-09-10 : `naturalWidth` valait 0, `complete` valait
 * `false`, et le `src` était pourtant le bon. La compilation n'en dit rien.
 *
 * ── CE QU'ON A ÉCARTÉ, ET POURQUOI ──────────────────────────────────────────
 * · Mettre le jeton dans l'URL (`?t=...`) : il finirait dans les journaux du
 *   serveur, dans l'historique du navigateur et dans tout mandataire traversé.
 * · Un jeton opaque par photo, sans authentification : une URL qui fuit rend la
 *   photo publique pour toujours. Une photographie est une donnée personnelle.
 * · Ouvrir la route sans authentification : hors de question.
 * · Repasser à du base64 dans la liste : un demi-mégaoctet à chaque clic de
 *   filtre, ce que la table dédiée avait justement écarté.
 *
 * ── CE QU'IL FAIT ───────────────────────────────────────────────────────────
 * Il demande l'image PAR axios — donc avec le jeton — et la transforme en URL
 * d'objet locale. Le cache est au niveau du MODULE, pas du composant : sans lui,
 * revenir sur l'onglet retéléchargerait les 72 images, et la liste et le
 * trombinoscope les demanderaient deux fois chacune.
 */

/** url de la route → url d'objet locale. Au niveau du module : la liste, la fiche
 *  et le trombinoscope regardent souvent les mêmes visages. */
const cache = new Map();

/** url de la route → promesse en cours. Sans cela, trois composants qui montent
 *  en même temps lancent trois fois la même requête. */
const enCours = new Map();

async function obtenir(chemin) {
    if (cache.has(chemin)) return cache.get(chemin);
    if (enCours.has(chemin)) return enCours.get(chemin);

    const promesse = api
        // `chemin` vaut « /api/etudiants/12/photo » et l'instance a « /api » pour
        // base : on retire le préfixe pour ne pas demander « /api/api/... ».
        .get(chemin.replace(/^\/api/, ''), { responseType: 'blob' })
        .then((r) => {
            const url = URL.createObjectURL(r.data);
            cache.set(chemin, url);
            enCours.delete(chemin);
            return url;
        })
        .catch((e) => {
            enCours.delete(chemin);
            // On mémorise l'échec : réessayer en boucle sur une photo illisible
            // (clé changée, ligne effacée) ferait 72 requêtes par rendu.
            cache.set(chemin, null);
            return null;
        });

    enCours.set(chemin, promesse);
    return promesse;
}

/**
 * À appeler après un import : `photo_url` ne change pas quand une photo est
 * REMPLACÉE — c'est la même route — donc le cache servirait l'ancienne image et
 * l'utilisateur croirait son import sans effet.
 */
export function viderCachePhotos() {
    for (const url of cache.values()) {
        if (url) URL.revokeObjectURL(url);
    }
    cache.clear();
    enCours.clear();
}

/**
 * `chemin` : la valeur de `photo_url`, ou rien.
 * `secours` : ce qu'on affiche tant qu'il n'y a pas d'image — les initiales dans
 *             la liste, une tuile pointillée dans le trombinoscope. C'est
 *             l'appelant qui sait, parce que la forme diffère à chaque endroit.
 */
function PhotoEtudiant({ chemin, secours, className, alt, title }) {
    const [url, setUrl] = useState(() => (chemin ? cache.get(chemin) || null : null));

    useEffect(() => {
        if (!chemin) { setUrl(null); return undefined; }

        let vivant = true;
        // Déjà en cache : on le pose tout de suite, sans clignotement.
        const dejaLa = cache.get(chemin);
        if (dejaLa !== undefined) { setUrl(dejaLa); return undefined; }

        obtenir(chemin).then((u) => { if (vivant) setUrl(u); });
        return () => { vivant = false; };
    }, [chemin]);

    // ⚠️ Les URL d'objet ne sont PAS révoquées au démontage : elles sont partagées
    // par le cache, et les libérer ici casserait l'image des autres composants qui
    // regardent le même étudiant. `viderCachePhotos` s'en charge, une fois.
    if (!chemin || !url) return secours || null;

    return <img className={className} src={url} alt={alt || ''} title={title} />;
}

export default PhotoEtudiant;
